import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "../DashBoardComponents/Navbar";
import { POSTS } from "./BlogData";

function Blog() {
  const navigate = useNavigate();
  const darkMode = localStorage.getItem("dox-theme") === "dark";

  const pageBg = darkMode ? "#0a0800" : "#f4f7fa";
  const cardBg = darkMode ? "#14110a" : "#fff";
  const cardBorder = darkMode ? "1px solid rgba(200,150,30,0.15)" : "1px solid rgba(0,0,0,0.06)";
  const textPrimary = darkMode ? "#f5e6b0" : "#071a3d";
  const textSecondary = darkMode ? "rgba(245,230,176,0.6)" : "#64748b";
  const accent = darkMode ? "#f0c040" : "#189FAA";

  return (
    <>
      <Navbar />
      <div style={{ background: pageBg, minHeight: "100vh" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
          .dox-blog * { font-family: 'Outfit', sans-serif; }
          .dox-blog-card { transition: transform 0.25s ease, box-shadow 0.25s ease; }
          .dox-blog-card:hover { transform: translateY(-4px); box-shadow: 0 14px 32px rgba(24,159,170,0.16); }
          .dox-blog-card:hover .dox-blog-title { color: #189FAA; }
          .dox-blog-card:hover .dox-blog-cover-img { transform: scale(1.06); }
          .dox-blog-card:hover .dox-avatar-ring { transform: scale(1.08) rotate(6deg); }
          .dox-blog-cover-img { transition: transform 0.4s ease; }
          .dox-avatar-ring { transition: transform 0.3s ease; }
          .dox-read-more { transition: gap 0.2s ease; }
          .dox-read-more:hover { gap: 8px; }
        `}</style>

        <div className="dox-blog">
          {/* ── Hero banner ── */}
          <div
            style={{
              background: darkMode
                ? "linear-gradient(135deg,#0f0b02,#1a1405)"
                : "linear-gradient(135deg,#071a3d 0%,#0d3b3e 55%,#189FAA 100%)",
              padding: "70px 24px 60px",
              textAlign: "center",
              marginTop: "70px",
            }}
          >
            <h1
              style={{
                fontSize: "44px",
                fontWeight: 800,
                color: "#fff",
                margin: "0 0 14px",
                letterSpacing: "-0.5px",
              }}
            >
               Healthcare  360°
            </h1>
            <p style={{ fontSize: "16px", color: "rgba(255,255,255,0.8)", maxWidth: "560px", margin: "0 auto" }}>
              Connecting Healthcare. Empowering Better Care..
            </p>
          </div>

          {/* ── Blog grid ── */}
          <div
            style={{
              maxWidth: "1180px",
              margin: "0 auto",
              padding: "56px 24px 40px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))",
              gap: "36px 28px",
            }}
          >
            {POSTS.map((post) => {
              const Icon = post.icon;
              return (
                <div
                  key={post.id}
                  className="dox-blog-card"
                  onClick={() => navigate(`/blog/${post.id}`)}
                  style={{
                    cursor: "pointer",
                    background: cardBg,
                    border: cardBorder,
                    borderRadius: "14px",
                    overflow: "visible",
                    paddingBottom: "20px",
                  }}
                >
                  {/* Cover image area */}
                  <div
                    style={{
                      position: "relative",
                      height: "190px",
                      borderRadius: "14px 14px 0 0",
                      overflow: "hidden",
                      background: post.gradient,
                    }}
                  >
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="dox-blog-cover-img"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 100%)",
                      }}
                    />

                    <span
                      style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        background: accent,
                        color: "#fff",
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        padding: "4px 10px",
                        borderRadius: "5px",
                        textTransform: "uppercase",
                      }}
                    >
                      Blog
                    </span>

                    <div
                      style={{
                        position: "absolute",
                        top: "12px",
                        left: "12px",
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        background: "rgba(255,255,255,0.18)",
                        backdropFilter: "blur(4px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon size={18} color="#fff" />
                    </div>

                    {/* Overlapping avatar — modern gradient ring + glow */}
                    <div
                      className="dox-avatar-ring"
                      style={{
                        position: "absolute",
                        bottom: "-18px",
                        left: "20px",
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        padding: "3px",
                        background: `linear-gradient(135deg, ${accent}, #7c3aed 65%, #ec4899)`,
                        boxShadow: darkMode
                          ? "0 8px 20px rgba(240,192,64,0.4), 0 0 0 4px rgba(240,192,64,0.08)"
                          : "0 8px 20px rgba(24,159,170,0.35), 0 0 0 4px rgba(24,159,170,0.08)",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                          overflow: "hidden",
                          border: `3px solid ${cardBg}`,
                          background: accent,
                        }}
                      >
                        <img
                          src={post.authorImage}
                          alt={post.author}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div style={{ padding: "42px 22px 0" }}>
                    <h3
                      className="dox-blog-title"
                      style={{
                        fontSize: "17px",
                        fontWeight: 700,
                        color: textPrimary,
                        margin: "0 0 10px",
                        lineHeight: 1.35,
                        transition: "color 0.2s ease",
                      }}
                    >
                      {post.title}
                    </h3>
                    <p
                      style={{
                        fontSize: "13.5px",
                        color: textSecondary,
                        lineHeight: 1.55,
                        marginBottom: "16px",
                      }}
                    >
                      {post.excerpt}
                    </p>
                    <div
                      className="dox-read-more"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/blog/${post.id}`);
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: accent,
                        marginBottom: "18px",
                      }}
                    >
                      READ MORE <ArrowRight size={13} />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        borderTop: darkMode ? "1px solid rgba(200,150,30,0.12)" : "1px solid rgba(0,0,0,0.06)",
                        paddingTop: "14px",
                        fontSize: "12px",
                        color: textSecondary,
                      }}
                    >
                      <span style={{ fontWeight: 600, color: textPrimary }}>{post.author}</span>
                      <span>|</span>
                      <span>{post.date}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Ready to get started CTA strip ── */}
          <div
            style={{
              maxWidth: "1180px",
              margin: "20px auto 0",
              padding: "0 24px 60px",
            }}
          >
            <div
              style={{
                background: darkMode ? "rgba(200,150,30,0.06)" : "#eef2f6",
                border: cardBorder,
                borderRadius: "16px",
                padding: "28px 32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <h3 style={{ fontSize: "22px", fontWeight: 700, color: textPrimary, margin: 0 }}>
                Ready to get started?
              </h3>
              <button
                onClick={() => navigate("/book-demo")}
                style={{
                  border: "none",
                  borderRadius: "999px",
                  padding: "12px 26px",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#fff",
                  cursor: "pointer",
                  background: darkMode
                    ? "linear-gradient(135deg,#3a2a05,#f0c040)"
                    : "linear-gradient(135deg,#0d3b3e,#189FAA,#1ec98f)",
                }}
              >
                Book a Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Blog;